import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { api } from '../../utils/apiClient';
import CountrySelector from '../common/CountrySelector';
import CountryConfirmationModal from '../modals/CountryConfirmationModal';
import { 
  getCountryNameFromCode, 
  isServiceAvailable, 
  SUPPORTED_SERVICES,
  SERVICE_DISPLAY_NAMES,
  getAllCountries,
  getSupportedCountries,
  getServiceAvailabilityInfo
} from '../../constants/countries';

/**
 * WithdrawalSetup component that allows users to select their country
 * before proceeding to the withdrawal options, or to choose a withdrawal method
 * if the country is already selected.
 * 
 * This page is only accessible to authenticated users.
 */
function WithdrawalSetup() {
  const { t } = useTranslation(['withdrawals']);
  const { user, setCountryCode } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [country, setCountry] = useState<string>('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showMethodInfo, setShowMethodInfo] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Ref to track if the component has mounted
  const isMountedRef = useRef(false);
  
  // Determine if we're in country selection mode or withdrawal selection mode
  const hasCountrySelected = !!user.countryCode;
  
  // Check if the user has already selected a country (saved in DB)
  useEffect(() => {
    // Skip if we already have a country code in the user context
    if (user.countryCode || !user.email || isMountedRef.current) {
      return;
    }
    
    const checkStripeStatus = async () => {
      try {
        setIsLoadingData(true);
        
        // Make sure user.email is not null before passing to API
        if (!user.email) {
          setIsLoadingData(false);
          return;
        }
        
        // Call the Stripe status API to get the country from DB
        const response = await api.wallet.getStripeAccountStatus(user.email);
        
        // If successful and we have a country, update the user context
        if (response.success && response.country !== null && response.country !== undefined) {
          // Only set if we have a valid string value
          setCountryCode(response.country);
          // No need to navigate - the hasCountrySelected will update and render the right view
        }
      } catch (error) {
        console.error('Error checking Stripe status:', error);
        // Silent fail - just let user pick country again if there's an error
      } finally {
        setIsLoadingData(false);
        // Mark that the initial check is complete
        isMountedRef.current = true;
      }
    };
    
    checkStripeStatus();
  }, [user.email, user.countryCode, setCountryCode]);
  
  // Check for URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasRedirectParam = params.get('redirect') === 'true';
    const hasRefreshParam = params.get('refresh') === 'true';
    
    // Clean up URL parameters immediately to prevent loops
    if (hasRedirectParam || hasRefreshParam) {
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show appropriate message
      if (hasRedirectParam) {
        setSuccessMessage(t('withdrawals.stripeRedirectInfo', 'You can resume your Stripe account setup anytime.'));
      } else if (hasRefreshParam) {
        setSuccessMessage(t('withdrawals.stripeRefreshInfo', 'The Stripe setup process was refreshed.'));
      }
    }
  }, [location.search, t]);
  
  // Set document title based on current mode
  useDocumentTitle(
    hasCountrySelected 
      ? t('withdrawals.withdrawalMethodsTitle', 'Select Withdrawal Method')
      : t('withdrawals.setupTitle', 'Withdrawal Setup')
  );
  
  // Handle country selection
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountry(e.target.value);
    setError('');
    
    // Show method info if a country is selected
    setShowMethodInfo(!!e.target.value);
  };
  
  // Handle showing the confirmation modal
  const handleShowConfirmModal = () => {
    if (!country) {
      setError(t('withdrawals.countryRequired', 'Please select your country to continue.'));
      return;
    }
    
    // Check if the country has any supported payment methods
    const { isSupported } = getServiceAvailabilityInfo(country);
    
    if (!isSupported) {
      setError(t('withdrawals.noSupportedMethodsError', 'This country doesn\'t have any supported withdrawal methods. Please select a different country.'));
      return;
    }
    
    // Show the confirmation modal
    setShowConfirmModal(true);
  };
  
  // Handle submitting the country selection after confirmation
  const handleCountrySubmit = () => {
    if (!user.email || !country) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Save the country code in the user context
      setCountryCode(country);
      
      // Show success message
      setSuccessMessage(t('withdrawals.countrySelectionSuccess', 'Your country has been saved. You can now choose a withdrawal method.'));
      
      // Close the modal
      setShowConfirmModal(false);
      
      // Reset loading state
      setLoading(false);
    } catch (err) {
      console.error('Error setting country:', err);
      setError(t('withdrawals.countrySelectionFailed', 'Failed to save country selection. Please try again.'));
      setLoading(false);
      setShowConfirmModal(false);
    }
  };
  
  // Get service availability for the selected country
  const serviceInfo = country 
    ? getServiceAvailabilityInfo(country)
    : { availableServices: [], displayNames: [], isSupported: false };
  
  // Get service availability for the user's already selected country
  const userCountryServiceInfo = user.countryCode
    ? getServiceAvailabilityInfo(user.countryCode)
    : { availableServices: [], displayNames: [], isSupported: false };
  
  // Statistics for the availability message
  const totalCountries = getAllCountries().length;
  const supportedCountries = getSupportedCountries().length;
  const stripeCountries = SUPPORTED_SERVICES.STRIPE.length;
  
  return (
    <div className="container py-5">
      {/* Header */}
      <div className="row mb-5">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Link to="/wallet" className="btn btn-outline-secondary me-3">
              <i className="bi bi-arrow-left"></i>
            </Link>
            <h3 className="fw-bold mb-0">
              {hasCountrySelected 
                ? t('withdrawals.withdrawalMethodsTitle', 'Select Withdrawal Method') 
                : t('withdrawals.setupTitle', 'Withdrawal Setup')}
            </h3>
          </div>
          <p className="text-body-secondary">
            {hasCountrySelected 
              ? t('withdrawals.withdrawalMethodsDescription', 'Choose your preferred method to withdraw your tokens')
              : t('withdrawals.setupDescription', 'Select your country before proceeding with withdrawals')}
          </p>
        </div>
      </div>
      
      {/* Status Messages */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-circle-fill me-2"></i>
              <div>{error}</div>
            </div>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-info d-flex align-items-center" role="alert">
              <i className="bi bi-info-circle-fill me-2"></i>
              <div>{successMessage}</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading Indicator */}
      {isLoadingData && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Country Selection Mode */}
      {!hasCountrySelected && !isLoadingData && (
        <div className="row">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-body border-bottom-0 py-4">
                <h2 className="h4 mb-0">{t('withdrawals.selectCountry', 'Select Your Country')}</h2>
              </div>
              <div className="card-body">
                <div className="alert alert-info border-0 mb-4">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle-fill fs-4 me-2"></i>
                    <div>
                      <div className="fw-medium mb-1">{t('withdrawals.countryImportant', 'Important')}</div>
                      <div className="small">
                        {t('withdrawals.countryExplanation', 'Your country selection will determine which withdrawal methods are available to you. For Stripe Connect withdrawals, you will need to provide verification documents for the selected country.')}
                      </div>
                      <div className="mt-2 small text-body-secondary">
                        <strong>{t('withdrawals.supportedCountries', 'Supported Countries')}:</strong> {supportedCountries} {t('withdrawals.outOf', 'out of')} {totalCountries} {t('withdrawals.countriesSupported', 'countries currently supported')}
                      </div>
                    </div>
                  </div>
                </div>
                
                <CountrySelector
                  value={country}
                  onChange={handleCountryChange}
                  label={t('withdrawals.country', 'Country')}
                  isLarge
                  required
                  error={error ? t('withdrawals.countryRequired', 'Please select your country to continue.') : ''}
                  // Show all countries that support at least one withdrawal method
                  showAllCountries={false}
                  showServiceIndicators={true}
                  helpText={t('withdrawals.countriesNotShown', 'Countries not shown are not currently supported for withdrawals.')}
                />
                
                {/* Payment Method Availability for Selected Country */}
                {showMethodInfo && country && (
                  <div className="mb-4 mt-4">
                    <div className="card border-0 bg-body-tertiary">
                      <div className="card-body">
                        <h6 className="mb-3">{t('withdrawals.availableMethodsForCountry', 'Available withdrawal methods for')} {getCountryNameFromCode(country)}:</h6>
                        
                        {serviceInfo.isSupported ? (
                          <>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                              {Object.keys(SUPPORTED_SERVICES).map(service => {
                                const serviceKey = service as keyof typeof SUPPORTED_SERVICES;
                                const isAvailable = serviceInfo.availableServices.includes(serviceKey);
                                
                                return (
                                  <div 
                                    key={service}
                                    className={`badge ${isAvailable ? 'bg-success' : 'bg-secondary'} p-2 d-flex align-items-center`}
                                  >
                                    <i className={`bi ${isAvailable ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
                                    <span>{SERVICE_DISPLAY_NAMES[serviceKey]}</span>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <p className="mb-0 small text-success">
                              <i className="bi bi-info-circle-fill me-1"></i>
                              {serviceInfo.displayNames.length > 1 
                                ? t('withdrawals.multipleMethodsAvailable', 'Multiple withdrawal methods are available for this country.') 
                                : t('withdrawals.oneMethodAvailable', 'One withdrawal method is available for this country.')}
                            </p>
                          </>
                        ) : (
                          <div className="alert alert-warning mb-0">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {t('withdrawals.noMethodsForCountry', 'There are currently no withdrawal methods available for this country. Please select a different country or check back later.')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="d-grid gap-2">
                  <button 
                    type="button" 
                    className="btn btn-primary btn-lg"
                    onClick={handleShowConfirmModal}
                    disabled={!country || loading || !serviceInfo.isSupported}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        {t('common.continue', 'Continue')}
                      </>
                    )}
                  </button>
                </div>
                
                <div className="alert alert-warning border-0 mt-4 mb-0">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill fs-4 me-2"></i>
                    <div>
                      <div className="fw-medium mb-1">{t('withdrawals.important', 'Important')}</div>
                      <div className="small">{t('withdrawals.countryWarning', 'Your country selection cannot be changed after a withdrawal method has been set up. Make sure you select the correct country.')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-body border-bottom-0 py-4">
                <h2 className="h4 mb-0">{t('withdrawals.availabilityInfo', 'Withdrawal Method Availability')}</h2>
              </div>
              <div className="card-body">
                <p className="text-body-secondary small mb-3">
                  {t('withdrawals.availabilityDescription', 'We currently support withdrawals in selected countries through the following methods:')}
                </p>
                
                {/* Only show methods with supported countries > 0 */}
                {stripeCountries > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-primary p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-bank me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Stripe Connect</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: stripeCountries })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        {t('withdrawals.stripeDescription', 'Bank transfers via Stripe Connect platform')}
                      </p>
                      <div className="mt-2">
                        <button 
                          className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target="#stripeCountriesList" 
                          aria-expanded="false"
                        >
                          <i className="bi bi-chevron-down me-1"></i>
                          {t('withdrawals.showCountries', 'Show countries')}
                        </button>
                        <div className="collapse mt-2" id="stripeCountriesList">
                          <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                            <p className="mb-0 small">
                              {SUPPORTED_SERVICES.STRIPE.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* Wise placeholder */}
                {SUPPORTED_SERVICES.WISE.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-info p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-globe me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Wise</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.WISE.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        International transfers at competitive exchange rates
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.WISE.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                          <button 
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#wiseCountriesList" 
                            aria-expanded="false"
                          >
                            <i className="bi bi-chevron-down me-1"></i>
                            {t('withdrawals.showCountries', 'Show countries')}
                          </button>
                        )}
                        {SUPPORTED_SERVICES.WISE.length > 0 && (
                          <div className="collapse mt-2" id="wiseCountriesList">
                            <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                              <p className="mb-0 small">
                                {SUPPORTED_SERVICES.WISE.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* Airwallex placeholder */}
                {SUPPORTED_SERVICES.AIRWALLEX.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-secondary p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-currency-exchange me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Airwallex</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.AIRWALLEX.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        Global business banking and international transfers
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.AIRWALLEX.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                          <button 
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#airwallexCountriesList" 
                            aria-expanded="false"
                          >
                            <i className="bi bi-chevron-down me-1"></i>
                            {t('withdrawals.showCountries', 'Show countries')}
                          </button>
                        )}
                        {SUPPORTED_SERVICES.AIRWALLEX.length > 0 && (
                          <div className="collapse mt-2" id="airwallexCountriesList">
                            <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                              <p className="mb-0 small">
                                {SUPPORTED_SERVICES.AIRWALLEX.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* Revolut placeholder */}
                {SUPPORTED_SERVICES.REVOLUT.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-dark p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-credit-card me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Revolut</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.REVOLUT.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        Digital banking with fast transfers and multiple currencies
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.REVOLUT.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                          <button 
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#revolutCountriesList" 
                            aria-expanded="false"
                          >
                            <i className="bi bi-chevron-down me-1"></i>
                            {t('withdrawals.showCountries', 'Show countries')}
                          </button>
                        )}
                        {SUPPORTED_SERVICES.REVOLUT.length > 0 && (
                          <div className="collapse mt-2" id="revolutCountriesList">
                            <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                              <p className="mb-0 small">
                                {SUPPORTED_SERVICES.REVOLUT.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* PayPal placeholder */}
                {SUPPORTED_SERVICES.PAYPAL.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-primary p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-paypal me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">PayPal</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.PAYPAL.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        Withdraw directly to your PayPal account
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.PAYPAL.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                          <button 
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#paypalCountriesList" 
                            aria-expanded="false"
                          >
                            <i className="bi bi-chevron-down me-1"></i>
                            {t('withdrawals.showCountries', 'Show countries')}
                          </button>
                        )}
                        {SUPPORTED_SERVICES.PAYPAL.length > 0 && (
                          <div className="collapse mt-2" id="paypalCountriesList">
                            <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                              <p className="mb-0 small">
                                {SUPPORTED_SERVICES.PAYPAL.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* Nium placeholder */}
                {SUPPORTED_SERVICES.NIUM.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-warning p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-arrow-left-right me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Nium</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.NIUM.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        Global payment infrastructure for cross-border transactions
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.NIUM.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                          <button 
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#niumCountriesList" 
                            aria-expanded="false"
                          >
                            <i className="bi bi-chevron-down me-1"></i>
                            {t('withdrawals.showCountries', 'Show countries')}
                          </button>
                        )}
                        {SUPPORTED_SERVICES.NIUM.length > 0 && (
                          <div className="collapse mt-2" id="niumCountriesList">
                            <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                              <p className="mb-0 small">
                                {SUPPORTED_SERVICES.NIUM.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* Thunes placeholder */}
                {SUPPORTED_SERVICES.THUNES.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-info p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-phone me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Thunes</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.THUNES.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        Emerging markets payment network with mobile wallet support
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.THUNES.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                          <button 
                            className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#thunesCountriesList" 
                            aria-expanded="false"
                          >
                            <i className="bi bi-chevron-down me-1"></i>
                            {t('withdrawals.showCountries', 'Show countries')}
                          </button>
                        )}
                        {SUPPORTED_SERVICES.THUNES.length > 0 && (
                          <div className="collapse mt-2" id="thunesCountriesList">
                            <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                              <p className="mb-0 small">
                                {SUPPORTED_SERVICES.THUNES.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                {/* Adyen placeholder */}
                {SUPPORTED_SERVICES.ADYEN.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="badge bg-success p-2 me-2 d-flex align-items-center">
                      <i className="bi bi-cash-stack me-1"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">Adyen</h6>
                      <p className="text-body-secondary small mb-1">
                        {t('withdrawals.supportedCountriesCount', 'Available in {{count}} countries', { count: SUPPORTED_SERVICES.ADYEN.length })}
                      </p>
                      <p className="text-body-secondary small mb-0">
                        Enterprise payment platform with global coverage
                      </p>
                      <div className="mt-2">
                        {SUPPORTED_SERVICES.ADYEN.length === 0 ? (
                          <p className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Coming soon
                          </p>
                        ) : (
                        <button 
                          className="btn btn-link btn-sm text-decoration-none p-0 text-body-secondary" 
                          type="button" 
                          data-bs-toggle="collapse" 
                            data-bs-target="#adyenCountriesList" 
                          aria-expanded="false"
                        >
                          <i className="bi bi-chevron-down me-1"></i>
                          {t('withdrawals.showCountries', 'Show countries')}
                        </button>
                        )}
                        {SUPPORTED_SERVICES.ADYEN.length > 0 && (
                          <div className="collapse mt-2" id="adyenCountriesList">
                          <div className="card card-body py-2 px-3 bg-body-tertiary border-0">
                            <p className="mb-0 small">
                                {SUPPORTED_SERVICES.ADYEN.map(code => getCountryNameFromCode(code)).sort().join(', ')}
                            </p>
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
                
                <div className="alert alert-light border-0 mb-4 small">
                  <strong>{t('withdrawals.countryAvailabilityNote', 'Country Availability')}:</strong><br/>
                  • {supportedCountries} {t('withdrawals.countriesSupported', 'countries supported for withdrawals')}<br/>
                  • {totalCountries - supportedCountries} {t('withdrawals.countriesNotSupported', 'countries not yet supported')}
                </div>
                
                <div className="alert alert-info border-0 mb-0 small">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  {t('withdrawals.addingMoreCountries', 'We\'re constantly working to add support for more countries and payment methods. If your country isn\'t supported yet, please check back later.')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Withdrawal Method Selection Mode */}
      {hasCountrySelected && !isLoadingData && (
        <div className="row">
          <div className="col-12 mb-4">
            <div className="alert alert-info border-0">
              <div className="d-flex align-items-center">
                <i className="bi bi-info-circle-fill fs-4 me-2"></i>
                <div>
                  <div className="fw-medium">
                    {t('withdrawals.countrySelectedInfo', 'Your selected country')}: {user.countryCode ? getCountryNameFromCode(user.countryCode) : ''}
                  </div>
                  <div className="small">
                    {userCountryServiceInfo.isSupported
                      ? t('withdrawals.availableMethodsInfo', 'Based on your country, the following withdrawal methods are available:')
                      : t('withdrawals.noMethodsAvailableInfo', 'Unfortunately, there are no withdrawal methods available for your country at this time.')}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Display Stripe Connect option if available for the country */}
          {userCountryServiceInfo.availableServices.includes('STRIPE') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-stripe me-2 text-primary"></i>
                    {t('withdrawals.stripeConnect', 'Stripe Connect')}
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>{t('withdrawals.stripeDescription', 'Withdraw your tokens directly to your bank account via Stripe. This method requires identity verification and bank account details.')}</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        {t('withdrawals.stripeBenefit1', 'Direct bank transfers')}
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        {t('withdrawals.stripeBenefit2', 'Secure and reliable')}
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        {t('withdrawals.stripeBenefit3', 'Available in most countries')}
                      </li>
                    </ul>
                    
                    <div className="alert alert-warning border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          {t('withdrawals.stripeRequiresVerification', 'Requires identity verification and bank account details')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Link to="/stripewithdrawal" className="btn btn-primary d-block">
                    <i className="bi bi-bank me-2"></i>
                    {t('withdrawals.continueToStripe', 'Continue with Stripe')}
                  </Link>
                </div>
              </div>
            </div>
          )}
          
          {/* Display Wise if available for the country */}
          {userCountryServiceInfo.availableServices.includes('WISE') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-globe me-2 text-info"></i>
                    Wise
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Withdraw your tokens with competitive exchange rates through Wise. Supports transfers to multiple currencies.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Low-cost international transfers
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Supports 50+ currencies
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Transparent exchange rates
                      </li>
                    </ul>
                    
                    <div className="alert alert-info border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires verification and bank account details
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-info d-block w-100 text-white">
                    <i className="bi bi-globe me-2"></i>
                    Continue with Wise
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display Revolut if available for the country */}
          {userCountryServiceInfo.availableServices.includes('REVOLUT') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-credit-card me-2 text-dark"></i>
                    Revolut
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Fast and secure transfers to your Revolut account with competitive exchange rates and support for multiple currencies.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Fast digital banking transfers
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Support for 30+ currencies
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        No hidden fees
                      </li>
                    </ul>
                    
                    <div className="alert alert-dark border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires a Revolut account linked to your phone number
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-dark d-block w-100">
                    <i className="bi bi-credit-card me-2"></i>
                    Continue with Revolut
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display Airwallex if available for the country */}
          {userCountryServiceInfo.availableServices.includes('AIRWALLEX') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-currency-exchange me-2 text-secondary"></i>
                    Airwallex
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Global business banking solution with international transfers and multi-currency capabilities.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        International transfers at competitive rates
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Supports global business transactions
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Multi-currency accounts
                      </li>
                    </ul>
                    
                    <div className="alert alert-secondary border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires business verification and account setup
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-secondary d-block w-100">
                    <i className="bi bi-currency-exchange me-2"></i>
                    Continue with Airwallex
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display PayPal if available for the country */}
          {userCountryServiceInfo.availableServices.includes('PAYPAL') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-paypal me-2 text-primary"></i>
                    PayPal
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Withdraw tokens directly to your PayPal account for easy access to your funds.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Fast transfers to your PayPal account
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Widely accepted payment method
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Link to bank accounts or cards
                      </li>
                    </ul>
                    
                    <div className="alert alert-primary border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires a verified PayPal account
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-primary d-block w-100">
                    <i className="bi bi-paypal me-2"></i>
                    Continue with PayPal
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display Nium if available for the country */}
          {userCountryServiceInfo.availableServices.includes('NIUM') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-arrow-left-right me-2 text-warning"></i>
                    Nium
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Global payment infrastructure for cross-border transactions with extensive regional coverage.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Fast cross-border payments
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Multi-currency support
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Competitive exchange rates
                      </li>
                    </ul>
                    
                    <div className="alert alert-warning border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires identity verification and bank account details
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-warning d-block w-100">
                    <i className="bi bi-arrow-left-right me-2"></i>
                    Continue with Nium
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display Thunes if available for the country */}
          {userCountryServiceInfo.availableServices.includes('THUNES') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-phone me-2 text-info"></i>
                    Thunes
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Payment network with strong presence in emerging markets and mobile wallet support.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Mobile wallet compatibility
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Extensive coverage in emerging markets
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Fast local payment options
                      </li>
                    </ul>
                    
                    <div className="alert alert-info border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires mobile number verification and local ID in some markets
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-info d-block w-100 text-white">
                    <i className="bi bi-phone me-2"></i>
                    Continue with Thunes
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display Adyen if available for the country */}
          {userCountryServiceInfo.availableServices.includes('ADYEN') && (
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-header bg-body border-bottom-0 py-4">
                  <h2 className="h4 mb-0">
                    <i className="bi bi-cash-stack me-2 text-success"></i>
                    Adyen
                  </h2>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <p>Enterprise payment platform with global coverage for secure and reliable transfers.</p>
                    
                    <ul className="list-unstyled mb-4">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Enterprise-grade security
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Global payment network
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Multiple currency support
                      </li>
                    </ul>
                    
                    <div className="alert alert-success border-0 mb-4">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <div className="small">
                          Requires account verification and bank details
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button className="btn btn-success d-block w-100">
                    <i className="bi bi-cash-stack me-2"></i>
                    Continue with Adyen
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Display a message if no withdrawal methods are available for this country */}
          {!userCountryServiceInfo.isSupported && (
            <div className="col-12 mb-4">
              <div className="alert alert-warning">
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill fs-4 me-2"></i>
                  <div>
                    <h5 className="alert-heading mb-2">{t('withdrawals.noMethodsAvailable', 'No Withdrawal Methods Available')}</h5>
                    <p className="mb-0">{t('withdrawals.noMethodsMessage', 'Unfortunately, we don\'t currently support any withdrawal methods for your selected country. We\'re working on expanding our coverage. Please check back later or contact support for assistance.')}</p>
                  </div>
                </div>
              </div>
              
              <div className="d-grid gap-2 mt-4">
                <button 
                  className="btn btn-outline-primary" 
                  onClick={() => setCountryCode('')}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  {t('withdrawals.selectDifferentCountry', 'Select a Different Country')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Country Confirmation Modal */}
      <CountryConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleCountrySubmit}
        countryName={country ? getCountryNameFromCode(country) : ''}
        isLoading={loading}
      />
    </div>
  );
}

export default WithdrawalSetup; 